"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

import AuthCard from "@/components/shared/auth-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { shouldReportAuthFlowExceptionToSentry } from "@/lib/auth-flow-sentry-filter";
import { createClient } from "@/lib/supabase/client";

type Strength = "weak" | "fair" | "strong";

function getStrength(pw: string): Strength {
  if (pw.length < 8) return "weak";
  const hasNumber = /\d/.test(pw);
  const hasSpecial = /[^a-zA-Z0-9]/.test(pw);
  const hasMixedCase = /[a-z]/.test(pw) && /[A-Z]/.test(pw);

  if ((hasNumber || hasSpecial) && hasMixedCase) return "strong";
  if (hasNumber || hasSpecial) return "fair";
  return "fair";
}

type FieldErrors = {
  name?: string;
  password?: string;
};

export default function SignupPage() {
  const router = useRouter();
  const supabase = useMemo(() => createClient(), []);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [showSignInInstead, setShowSignInInstead] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const strength = getStrength(password);
  const strengthUi =
    strength === "weak"
      ? { bar: "bg-red-500", label: "Weak", text: "text-red-500" }
      : strength === "fair"
        ? { bar: "bg-amber-500", label: "Fair", text: "text-amber-600" }
        : { bar: "bg-green-500", label: "Strong", text: "text-green-600" };

  function validate(): boolean {
    const next: FieldErrors = {};
    const trimmedName = name.trim();

    if (trimmedName.length < 2) next.name = "Name must be at least 2 characters.";
    if (password.length < 8) next.password = "Password must be at least 8 characters.";

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setShowSignInInstead(false);
    if (!validate()) return;
    setSubmitting(true);

    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            full_name: name.trim(),
          },
        },
      });

      if (authError) {
        const msg = authError.message.toLowerCase();
        if (
          msg.includes("already registered") ||
          msg.includes("already exists") ||
          msg.includes("user already registered")
        ) {
          setError("An account with this email already exists.");
          setShowSignInInstead(true);
          return;
        }
        if (msg.includes("rate limit") || msg.includes("over_email_send_rate_limit")) {
          setError("Too many attempts. Please wait a few minutes and try again.");
          return;
        }
        setError(authError.message);
        return;
      }

      const userId = data.user?.id;
      if (!userId) {
        setSent(true);
        setError("Please check your email to confirm your account");
        return;
      }

      posthog.identify(userId, { email: data.user?.email });
      router.push("/dashboard");
    } catch (e) {
      if (shouldReportAuthFlowExceptionToSentry(e)) {
        Sentry.captureException(e);
      }
      const msg = e instanceof Error ? e.message : "Unexpected error";
      const lowered = msg.toLowerCase();
      if (
        lowered.includes("already registered") ||
        lowered.includes("already exists") ||
        lowered.includes("user already registered")
      ) {
        setError("An account with this email already exists.");
        setShowSignInInstead(true);
        return;
      }
      if (lowered.includes("rate limit") || lowered.includes("over_email_send_rate_limit")) {
        setError("Too many attempts. Please wait a few minutes and try again.");
        return;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <AuthCard
        title="Create your account"
        subtitle="Start building forms that feel human."
      >
        {sent ? (
          <p className="text-center text-sm text-muted-foreground">
            {error ?? "Please check your email to confirm your account"}
          </p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <Input
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-11"
              />
              {errors.name ? (
                <p className="mt-1 text-sm text-destructive">{errors.name}</p>
              ) : null}
            </div>

            <div>
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
              />
            </div>

            <div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Set your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>

              <div className="mt-2 flex items-center justify-between gap-3">
                {password.length > 0 ? (
                  <>
                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-border">
                      <div
                        className={[
                          "h-full transition-all",
                          strengthUi.bar,
                          strength === "weak"
                            ? "w-1/3"
                            : strength === "fair"
                              ? "w-2/3"
                              : "w-full",
                        ].join(" ")}
                      />
                    </div>
                    <span
                      className={["text-xs font-medium", strengthUi.text].join(" ")}
                    >
                      {strengthUi.label}
                    </span>
                  </>
                ) : null}
              </div>

              {errors.password ? (
                <p className="mt-1 text-sm text-destructive">{errors.password}</p>
              ) : null}
            </div>

            {error ? (
              <div className="space-y-1">
                <p className="text-sm text-destructive">{error}</p>
                {showSignInInstead ? (
                  <Link href="/login" className="text-sm text-primary">
                    Sign in instead →
                  </Link>
                ) : null}
              </div>
            ) : null}

            <Button
              type="submit"
              className="h-11 w-full bg-primary text-base font-medium text-primary-foreground hover:bg-primary/90"
              disabled={submitting}
            >
              Create account
            </Button>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link
                href="/login"
                className="font-medium text-primary hover:underline"
              >
                Sign in →
              </Link>
            </p>
          </form>
        )}
      </AuthCard>
    </>
  );
}

